<template>
  <OnboardingFormStep card-heading="Enter Your Location" class="narrow" :commit="commit">
	
	<template #heading>
		<h1 class="onboarding-step-heading">I'm based in...</h1>
	</template>

    <template #notice-top v-if="!isStudio && (isNewAccount || isUpgrading)">
		<div class="notice-green mx-auto text-center">
			<p class="fs-14 lh-22 m0">
				<strong>
				<template v-if="isUpgrading">Let’s get you discovered!</template>
				<template v-else>Congrats on your account! Now let’s get you discovered!</template>
				</strong>
			</p>
			<p class="fs-14 lh-22 mt-16">
				First, you’ll need to create a profile that studios will use to see if you’re a good fit for
				one of their jobs. Completing the profile accurately is very important to getting discovered
				and offered a job.
			</p>
		</div>
    </template>

    <CountryField v-model="country" :error.sync="countryError" required />
    <PostalCodeField
      v-model="postalCode"
      :country="country"
      :error.sync="postalCodeError"
      required
    />
  </OnboardingFormStep>
</template>

<script>
import { toCountry } from '@/helpers/geo';
import { pick } from '@/helpers/reflect';
import onboarding from '@/mixins/onboarding';

export default {
  name: 'OnboardingLocation',
  mixins: [onboarding],
  onboardingStep: {
    isCompleted: ({ plan, profile }) =>
      plan && (!plan.requiresLocation || (profile && profile.country && profile.zip)),
    requires: ({ plan, profile }) => plan && profile,
  },
  data() {
    return {
      /** @type {string | null} */
      countryError: null,
      /** @type {string | null} */
      postalCodeError: null,
    };
  },
  computed: {
    country: {
      /**
       * @returns {import('@/helpers/geo').Country | null}
       */
      get() {
        /** @type {import('@/mixins/onboarding').OnboardingComponent} */
        const { profile } = this;
        return profile ? toCountry(profile.country) : null;
      },
      /**
       * @param {import('@/helpers/geo').Country | null} value
       * @returns {void}
       */
      set(value) {
        /** @type {import('@/mixins/onboarding').OnboardingComponent} */
        const { profile } = this;
        if (profile) {
          profile.country = value;
        }
      },
    },
    postalCode: {
      /**
       * @returns {string | null}
       */
      get() {
        /** @type {import('@/mixins/onboarding').OnboardingComponent} */
        const { profile } = this;
        return (profile && profile.zip) || null;
      },
      /**
       * @param {string | null} value
       * @returns {void}
       */
      set(value) {
        /** @type {import('@/mixins/onboarding').OnboardingComponent} */
        const { profile } = this;
        if (profile) {
          profile.zip = value;
        }
      },
    },
  },
  methods: {
    /**
     * @returns {boolean}
     */
    async commit() {
      /** @type {{ country: import('@/helpers/geo').Country | null; postalCode: string | null; profile: Profile | null }} */
      const { country, postalCode, profile } = this;
      if (!profile) {
        return false;
      }

      let valid = true;

      if (!country) {
        this.countryError = 'Please select your country.';
        valid = false;
      }

      if (
        !postalCode ||
        postalCode === '00000' ||
        (country && !country.isValidPostalCode(postalCode))
      ) {
        const words = country ? [country.adjective, country.postalCodeNoun] : ['postal code'];
        this.postalCodeError = `Please enter a valid ${words.join(' ')}.`;
        valid = false;
      } else if (!(await this.validatePostalCode(postalCode))) {
        const noun = country ? country.postalCodeNoun : 'postal code';
        this.postalCodeError = `ShootWith.Me isn't available in this ${noun}.`;
        valid = false;
      }

      if (!valid) {
        return false;
      }

      try {
        /** @type {(keyof ProfilePatch)[]} */
        const keys = ['country', 'timezone', 'zip'];
        const store = this.$store;

        /** @type {import('luxon').Zone | null} */
        let zone = null;

        /** @type {import('@/store/i18n/actions').PostalCodeLocation | null} */
        const location = await store.dispatch('i18n/getLocationFromPostalCode', {
          country: country.alpha2,
          postalCode,
        });
        if (
          location &&
          (toCountry(location.country) === country || toCountry(location.countryShort) === country)
        ) {
          keys.push('city', 'state');
          profile.city = location.locality;
          profile.state = location.region;
          if (!profile.state) {
            profile.state = location.regionShort;
          }
          zone = await store.dispatch('i18n/getTimeZoneFromLocation', location.coordinates);
        }

        profile.timezone = zone ? zone.name : null;
        await this.$store.dispatch('profiles/updateActiveProfile', pick(profile.toPatch(), keys));

        this.$gtmDispatchOnboardingStepEvent(3, 'Location Input');

        return true;
      } catch (error) {
        this.error = 'An unknown error occurred. Please try again later.';
        console.error(error);
        return false;
      }
    },
    /**
     * @param {string} postalCode
     * @returns {Promise<boolean>}
     */
    async validatePostalCode(postalCode) {
      try {
        return await this.$store.dispatch('profiles/validateZipCode', postalCode);
      } catch (e) {
        return true;
      }
    },
  },
};
</script>
